import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiConfig from '../config/api.config';

interface Proveedor {
  id: number;
  razon_social: string;
  nit: string;
  email: string;
  telefono: string;
  tipo_proveedor: string;
  direccion: string;
  tamano_flota: number;
  username: string;
  estado: string;
}

interface AuthContextType {
  proveedor: Proveedor | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, recaptchaToken?: string) => Promise<void>;
  loginWithAzure: (token: string, userData: any) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [proveedor, setProveedor] = useState<Proveedor | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!token && !!proveedor;

  // Cargar token del localStorage al inicializar
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    const savedProveedor = localStorage.getItem('auth_proveedor');
    
    if (savedToken && savedProveedor) {
      try {
        setToken(savedToken);
        setProveedor(JSON.parse(savedProveedor));
      } catch (error) {
        console.error('Error parsing saved auth data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_proveedor');
      }
    }
    
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string, recaptchaToken?: string): Promise<void> => {
    try {
      console.log('🔐 Intentando login con:', { username, password: '***', recaptcha: recaptchaToken ? 'presente' : 'no enviado' });
      
      // Preparar datos de login
      const loginData: any = { username, password };
      
      // Agregar token de reCAPTCHA si está presente
      if (recaptchaToken) {
        loginData.recaptcha_token = recaptchaToken;
      }
      
      const response = await fetch(apiConfig.endpoints.login, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error en el login');
      }

      setToken(data.access_token);
      setProveedor(data.proveedor);
      
      // Guardar en localStorage
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('auth_proveedor', JSON.stringify(data.proveedor));
      
      console.log('✅ Login exitoso:', data.proveedor.username);
    } catch (error) {
      console.error('❌ Error en login:', error);
      throw error;
    }
  };

  const loginWithAzure = async (token: string, userData: any): Promise<void> => {
    try {
      console.log('🔐 Login con Azure AD:', userData);
      
      setToken(token);
      setProveedor(userData);
      
      // Guardar en localStorage
      localStorage.setItem('auth_token', token);
      localStorage.setItem('auth_proveedor', JSON.stringify(userData));
      
      console.log('✅ Login Azure exitoso:', userData.username);
    } catch (error) {
      console.error('❌ Error en login Azure:', error);
      throw error;
    }
  };

  const register = async (data: any): Promise<void> => {
    try {
      const response = await fetch(apiConfig.endpoints.registroSimple, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error en el registro');
      }

      console.log('✅ Registro exitoso:', result.username);
      
      // NO hacer login automático aquí - esperar a que complete el formulario de sostenibilidad
      // El login se hará después del formulario de sostenibilidad
    } catch (error) {
      console.error('❌ Error en registro:', error);
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setProveedor(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_proveedor');
    console.log('👋 Logout exitoso');
  };

  const value = {
    proveedor,
    token,
    isAuthenticated,
    isLoading,
    login,
    loginWithAzure,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;